import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Customer, LoginCredentials, SignupData } from '../types';
import authService from '../services/auth';

interface AuthContextType {
  user: Customer | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  signup: (data: SignupData) => Promise<{ id: string; email: string; message: string }>;
  verifyEmail: (token: string) => Promise<{ message: string }>;
  resendVerification: (email: string) => Promise<{ message: string }>;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = async () => {
    // Skip auth check on public pages where it's expected to fail.
    // Including '/' avoids a noisy 401 on the public landing page for visitors.
    const publicPaths = ['/', '/signup', '/login', '/verify-email', '/forgot-password', '/reset-password', '/terms', '/privacy'];
    const currentPath = window.location.pathname;

    if (publicPaths.includes(currentPath)) {
      setLoading(false);
      return;
    }

    try {
      const currentUser = await authService.getCurrentUser();
      // If we're authenticated but missing a CSRF token in sessionStorage
      // (e.g. after a tab refresh that kept the JWT cookie but cleared sessionStorage),
      // proactively fetch one so the next state-changing request doesn't 403.
      if (currentUser && !sessionStorage.getItem('csrfToken')) {
        try {
          await authService.refreshCSRFToken();
        } catch (csrfError) {
          // Non-fatal; the api interceptor will retry on 403 if needed.
        }
      }
      // Only update state if user data has actually changed
      setUser(prevUser => {
        if (!prevUser && !currentUser) return null;
        if (!prevUser && currentUser) return currentUser;
        if (prevUser && !currentUser) return null;
        // Check if user data has changed (both are non-null at this point)
        if (prevUser && currentUser &&
            prevUser.id === currentUser.id &&
            prevUser.catalogAccessLevel === currentUser.catalogAccessLevel &&
            prevUser.email === currentUser.email) {
          return prevUser; // No change, keep existing reference
        }
        return currentUser;
      });
    } catch (error: any) {
      // Only log non-401 errors (401 is expected when not logged in)
      if (error.response?.status !== 401) {
        console.error('Auth check error:', error);
      }
      setUser(prevUser => prevUser ? null : prevUser); // Only update if actually logged in
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Separate effect for periodic auth check
  useEffect(() => {
    if (!user) return;
    
    // Set up periodic auth check every 30 seconds to detect access level changes
    const interval = setInterval(() => {
      checkAuth();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [user?.id]); // Only re-run if user id changes (login/logout)

  const login = async (credentials: LoginCredentials) => {
    const user = await authService.login(credentials);
    setUser(user);
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
  };

  const signup = async (data: SignupData) => {
    return await authService.signup(data);
  };

  const verifyEmail = async (token: string) => {
    return await authService.verifyEmail(token);
  };

  const resendVerification = async (email: string) => {
    return await authService.resendVerification(email);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    signup,
    verifyEmail,
    resendVerification,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};